<template>
  <!-- Only rendered for tenants with a platform billing model (grassroots). -->
  <div class="payments-card platform-billing" v-if="billing">
    <div class="card-header-row">
      <div class="card-title-left">
        <i class="bi bi-wallet2 pb-icon"></i>
        <h3>Your platform bill</h3>
      </div>
    </div>

    <!-- Bundled model: the app fee is added on top of members' fees, so there's
         nothing for the club to set up or pay separately. -->
    <template v-if="isBundled">
      <p class="muted" v-if="isAbsorbed">
        Your club covers a <strong>{{ unitLabel }} per player</strong> app fee
        for its members. Families pay only your club fee — the app fee comes out
        of each player's first season-fee payment automatically, and there's
        nothing to set up here.
      </p>
      <p class="muted" v-else-if="isIncluded">
        Each member pays a <strong>{{ unitLabel }} per player</strong> app fee,
        included in the first season-fee payment — families see one amount with
        no separate line, and the fee comes to us automatically. Your club keeps
        100% of its fees, and there's nothing to set up here.
      </p>
      <p class="muted" v-else>
        Each member pays a <strong>{{ unitLabel }} per player</strong> app fee,
        added on top of their season fee at checkout — it's shown as its own line
        and comes to us automatically. Your club keeps 100% of its fees, and there's
        nothing to set up here.
      </p>
    </template>

    <template v-else>
    <p class="muted">
      {{ unitLabel }} per active player each month (capped) — your club's
      subscription to The Performance Centre. We take nothing from your members'
      payments.
    </p>

    <p class="muted" v-if="loading">Loading…</p>

    <template v-else>
      <div class="pb-summary">
        <div class="pb-figure">
          <span class="pb-num">{{ playerCount }}</span>
          <span class="pb-sub">active players</span>
        </div>
        <div class="pb-figure">
          <span class="pb-num">{{ money(monthlyFeePence) }}</span>
          <span class="pb-sub">per month<span v-if="capped"> (capped)</span></span>
        </div>
        <div class="pb-figure">
          <span class="status-pill" :class="statusClass">{{ statusLabel }}</span>
        </div>
      </div>

      <p v-if="status === 'past_due'" class="pb-warn">
        <i class="bi bi-exclamation-triangle"></i>
        Your last payment failed — update your payment method to keep the club active.
      </p>

      <div class="pb-actions">
        <button
          v-if="status === 'none' || status === 'canceled'"
          class="action-chip confirm"
          :disabled="busy"
          @click="setUpBilling"
        >
          {{ busy ? "Opening…" : "Set up billing" }}
        </button>
        <button v-else class="action-chip" :disabled="busy" @click="managePayment">
          {{ busy ? "Opening…" : "Manage payment / invoices" }}
        </button>
      </div>

      <p class="ok-text" v-if="justCompleted">Thanks — your billing is set up. 🎉</p>
      <p class="error-text" v-if="error">{{ error }}</p>
    </template>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getPlatformBilling } from "@/config/tenant";
import { PlatformBilling } from "@/services/platform-billing-service.js";

export default {
  name: "ClubPlatformBilling",
  props: {
    clubId: { type: String, required: true },
  },
  data() {
    return {
      billing: getPlatformBilling(), // null for non-grassroots → card hidden
      loading: true,
      busy: false,
      error: "",
      status: "none",
      playerCount: 0,
      monthlyFeePence: 0,
      currency: "gbp",
      capped: false,
      justCompleted: false,
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isBundled() {
      return (this.billing?.model || "") === "bundled";
    },
    isIncluded() {
      return this.isBundled && this.billing?.presentation === "included";
    },
    isAbsorbed() {
      return this.isBundled && this.billing?.presentation === "absorbed";
    },
    unitLabel() {
      if (!this.billing) return "";
      const symbol = { gbp: "£", aud: "$", usd: "$", eur: "€", nzd: "$" }[
        (this.billing.currency || "gbp").toLowerCase()
      ] || "£";
      const pence = this.isBundled
        ? this.billing.seasonFeePence
        : this.billing.unitAmountPence;
      return symbol + (pence / 100).toFixed(2).replace(/\.00$/, "");
    },
    statusLabel() {
      return {
        none: "Not set up",
        pending: "Awaiting payment",
        active: "Active",
        past_due: "Payment failed",
        canceled: "Cancelled",
      }[this.status] || this.status;
    },
    statusClass() {
      if (this.status === "active") return "good";
      if (this.status === "past_due") return "bad";
      if (this.status === "pending") return "warn";
      return "neutral";
    },
  },
  created() {
    if (!this.billing) return;
    // Bundled tenants have no separate subscription — nothing to load.
    if (this.isBundled) {
      this.loading = false;
      return;
    }
    // Returning from Stripe Checkout?
    const params = new URLSearchParams(window.location.search);
    if (params.get("platform_billing") === "done") this.justCompleted = true;
    this.loadStatus();
  },
  methods: {
    money(pence) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: (this.currency || "gbp").toUpperCase(),
        minimumFractionDigits: (pence || 0) % 100 === 0 ? 0 : 2,
      }).format((pence || 0) / 100);
    },
    async loadStatus() {
      this.loading = true;
      try {
        const res = await PlatformBilling.getStatus();
        const d = res.data.data || {};
        this.status = d.status || "none";
        this.playerCount = d.playerCount || 0;
        this.monthlyFeePence = d.monthlyFeePence || 0;
        this.currency = d.currency || this.billing.currency || "gbp";
        this.capped = !!d.capped;
      } catch (e) {
        this.error = "Couldn't load your billing status.";
      } finally {
        this.loading = false;
      }
    },
    async setUpBilling() {
      this.error = "";
      this.busy = true;
      try {
        const email = this.getProfile?.email || "";
        const res = await PlatformBilling.createCheckout(email);
        if (res.data?.data?.url) {
          window.location.href = res.data.data.url;
        } else {
          this.error = res.data?.error || "Could not start billing setup.";
        }
      } catch (e) {
        this.error =
          e?.response?.data?.error || "Could not start billing setup.";
      } finally {
        this.busy = false;
      }
    },
    async managePayment() {
      this.error = "";
      this.busy = true;
      try {
        const res = await PlatformBilling.createPortal();
        if (res.data?.data?.url) {
          window.location.href = res.data.data.url;
        } else {
          this.error = res.data?.error || "Could not open the billing portal.";
        }
      } catch (e) {
        this.error =
          e?.response?.data?.error || "Could not open the billing portal.";
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.platform-billing {
  .card-header-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;

    .card-title-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    h3 { margin: 0; }
    .pb-icon { font-size: 1.7rem; color: #16a34a; }
  }

  .muted { color: #777; font-size: 0.9rem; }
  .error-text { color: #c0392b; font-size: 0.85rem; }
  .ok-text { color: #1e7b3c; font-size: 0.9rem; }

  .pb-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem 2.5rem;
    align-items: center;
    margin: 0.75rem 0;
  }
  .pb-figure {
    display: flex;
    flex-direction: column;
    .pb-num { font-size: 1.5rem; font-weight: 700; color: #1a202c; line-height: 1.1; }
    .pb-sub { font-size: 0.8rem; color: #777; }
  }

  .pb-warn {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b03030;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    i { margin-right: 0.35rem; }
  }

  .pb-actions { margin-top: 0.6rem; }

  .status-pill {
    display: inline-block;
    padding: 3px 12px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    &.good { background: #d1e7dd; color: #0f5132; }
    &.bad { background: #f8d7da; color: #842029; }
    &.warn { background: #fff3cd; color: #856404; }
    &.neutral { background: #e9ecef; color: #495057; }
  }

  .action-chip {
    border: 1px solid #ccc;
    background: #fff;
    color: #444;
    border-radius: 6px;
    padding: 0.4rem 0.9rem;
    font-size: 0.85rem;
    cursor: pointer;

    &.confirm {
      background: #16a34a;
      border-color: #16a34a;
      color: #fff;
      &:hover:not(:disabled) { background: #15803d; }
    }
    &:disabled { opacity: 0.6; cursor: default; }
  }
}
</style>
